import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { adaptEchartsOptions, BaseEchartsDirective } from '@examdojo/core/widget';
import { getTailwindColorValue } from '@examdojo/styles';
import { EChartsOption } from 'echarts';
import { TreeChart } from 'echarts/charts';
import { TooltipComponent, ToolboxComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import katex from 'katex';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import type { ThemeColorName } from '@examdojo/styles';
import type { TreeNodeDefinition, TreeParams, TreePath, TreePathColors } from './tree.model';

echarts.use([TreeChart, TooltipComponent, ToolboxComponent]);

// ── Helpers ──────────────────────────────────────────────

function isOnPath(nodePath: readonly number[], target: TreePath): boolean {
  if (nodePath.length > target.length) {
    return false;
  }
  return nodePath.every((v, i) => v === target[i]);
}

function computeDepth(def: TreeNodeDefinition): number {
  if (!def.children || def.children.length === 0) {
    return 0;
  }
  return 1 + Math.max(...def.children.map((e) => computeDepth(e.node)));
}

function autoNodeRadius(depth: number): number {
  if (depth <= 2) {
    return 10;
  }
  if (depth <= 3) {
    return 8;
  }
  if (depth <= 4) {
    return 6;
  }
  if (depth <= 5) {
    return 5;
  }
  return 4;
}

// ── ECharts data conversion ──────────────────────────────

interface EchartsTreeData {
  name: string;
  symbol: string;
  symbolSize: number;
  itemStyle: Record<string, unknown>;
  lineStyle?: Record<string, unknown>;
  label: Record<string, unknown>;
  children?: EchartsTreeData[];
  collapsed: boolean;
  /** Edge labels accumulated from root to this node (for tooltip). */
  edgeLabelsFromRoot: string[];
}

interface BuildContext {
  matchingPaths: TreePath[];
  matchingPathColors: TreePathColors[];
  hoveredPath: TreePath | null;
  matchColor: string;
  hoverColor: string;
  nodeRadius: number;
  showLeafProb: boolean;
  showLeafEdgeLabels: boolean;
  isDark: boolean;
  labelBg: string;
}

function computeHighlight(currentPath: number[], isRoot: boolean, isLeaf: boolean, ctx: BuildContext) {
  const hasMatch = ctx.matchingPaths.length > 0;
  const onMatch = hasMatch && ctx.matchingPaths.some((p) => isOnPath(currentPath, p));
  const onHover = ctx.hoveredPath ? isOnPath(currentPath, ctx.hoveredPath) : false;
  const anyHover = ctx.hoveredPath !== null;

  // Node colors
  const defaultColor = getTailwindColorValue('neutral', 200);
  const defaultStroke = getTailwindColorValue('neutral', 300);
  let nodeColor = defaultColor;
  let nodeStroke = defaultStroke;

  if (onHover) {
    nodeColor = ctx.hoverColor;
    nodeStroke = ctx.hoverColor;
  } else if (onMatch) {
    // Use per-depth color if available (root uses first draw's color)
    const matchIdx = ctx.matchingPaths.findIndex((p) => isOnPath(currentPath, p));
    const depthColors = matchIdx >= 0 ? ctx.matchingPathColors[matchIdx] : undefined;
    const depthColor = depthColors?.[Math.max(0, currentPath.length - 1)];
    const resolvedMatchColor = depthColor ? getTailwindColorValue(depthColor) : ctx.matchColor;
    nodeColor = resolvedMatchColor;
    nodeStroke = resolvedMatchColor;
  }

  let nodeOpacity = 1;
  if (anyHover && !onHover) {
    nodeOpacity = isLeaf ? 0.3 : 0.6;
  }

  // Edge colors
  const defaultEdgeColor = getTailwindColorValue('neutral', 300);
  let edgeColor = defaultEdgeColor;
  let edgeWidth = 1.5;

  if (!isRoot) {
    if (onHover) {
      edgeColor = ctx.hoverColor;
      edgeWidth = 3;
    } else if (onMatch) {
      // Use per-depth color for edge (edge leads TO this node, so use depth - 1)
      const matchIdx = ctx.matchingPaths.findIndex((p) => isOnPath(currentPath, p));
      const depthColors = matchIdx >= 0 ? ctx.matchingPathColors[matchIdx] : undefined;
      const depthColor = depthColors?.[currentPath.length - 1];
      edgeColor = depthColor ? getTailwindColorValue(depthColor) : ctx.matchColor;
      edgeWidth = 2;
    } else if (anyHover) {
      edgeColor = getTailwindColorValue('neutral', 100);
    }
  }

  return { nodeColor, nodeStroke, nodeOpacity, edgeColor, edgeWidth, onMatch, onHover, anyHover };
}

/** Check if a label string contains LaTeX notation. */
function isLatexLabel(label: string): boolean {
  return label.includes('^{') || label.includes('_{') || label.includes('\\');
}

function buildEchartsData(
  def: TreeNodeDefinition,
  edgeLabel: string | undefined,
  currentPath: number[],
  edgeLabelsAlongPath: string[],
  ctx: BuildContext,
): EchartsTreeData {
  const isLeaf = !def.children || def.children.length === 0;
  const isRoot = currentPath.length === 0;

  const hl = computeHighlight(currentPath, isRoot, isLeaf, ctx);

  // ── Override node color for colored content ──
  if (def.content?.color && !hl.onHover && !hl.onMatch) {
    const contentColor = getTailwindColorValue(def.content.color);
    hl.nodeColor = contentColor;
    hl.nodeStroke = contentColor;
  }

  // ── Build label ──
  // Resolve per-depth match color for labels
  let resolvedEdgeMatchColor = ctx.matchColor;
  if (hl.onMatch) {
    const matchIdx = ctx.matchingPaths.findIndex((p) => isOnPath(currentPath, p));
    const depthColors = matchIdx >= 0 ? ctx.matchingPathColors[matchIdx] : undefined;
    const depthColor = depthColors?.[Math.max(0, currentPath.length - 1)];
    if (depthColor) {
      resolvedEdgeMatchColor = getTailwindColorValue(depthColor);
    }
  }
  const labelBg = ctx.labelBg;
  const edgeLabelColor = hl.onMatch
    ? resolvedEdgeMatchColor
    : hl.onHover
      ? ctx.hoverColor
      : getTailwindColorValue('neutral', 600);
  const contentColor = def.content?.color
    ? getTailwindColorValue(def.content.color)
    : hl.onMatch
      ? ctx.matchColor
      : getTailwindColorValue('neutral', 600);

  const hasEdgeLabel = !!edgeLabel && !isRoot;
  const contentLabel = def.content?.label;
  const hasLeafProb = isLeaf && ctx.showLeafProb && edgeLabelsAlongPath.length > 0;

  let nodeLabel: Record<string, unknown>;

  if (hasEdgeLabel && contentLabel) {
    const isLatex = isLatexLabel(contentLabel);
    if (isLatex) {
      // LaTeX content rendered as HTML overlay; show edge label only
      nodeLabel = {
        show: true,
        position: 'top',
        distance: 4,
        formatter: edgeLabel,
        fontSize: 10,
        fontWeight: 500,
        color: edgeLabelColor,
        backgroundColor: labelBg,
        padding: [1, 4],
        borderRadius: 2,
      };
    } else {
      // Both edge label and content → edge above, content below via rich text
      const nodeSize = ctx.nodeRadius * 2;
      nodeLabel = {
        show: true,
        position: 'inside',
        formatter: `{edge|${edgeLabel}}\n{spacer| }\n{content|${contentLabel}}`,
        rich: {
          edge: {
            fontSize: 10,
            fontWeight: 500,
            color: edgeLabelColor,
            backgroundColor: labelBg,
            padding: [1, 4],
            borderRadius: 2,
            align: 'center',
            lineHeight: 14,
          },
          spacer: {
            lineHeight: nodeSize,
            align: 'center',
          },
          content: {
            fontSize: 11,
            fontWeight: isLeaf ? 400 : 'bold',
            color: contentColor,
            align: 'center',
            lineHeight: isLeaf ? 15 : 14,
          },
        },
      };
    }
  } else if (hasEdgeLabel) {
    // Only edge label → show above node (on the line)
    nodeLabel = {
      show: true,
      position: 'top',
      distance: 4,
      formatter: edgeLabel,
      fontSize: 10,
      fontWeight: 500,
      color: edgeLabelColor,
      backgroundColor: labelBg,
      padding: [1, 4],
      borderRadius: 2,
    };
  } else if (contentLabel) {
    if (isLatexLabel(contentLabel)) {
      // LaTeX content rendered as HTML overlay; hide ECharts label
      nodeLabel = { show: false };
    } else {
      nodeLabel = {
        show: true,
        position: isRoot ? 'top' : 'bottom',
        distance: 4,
        formatter: contentLabel,
        fontSize: isRoot ? 12 : 11,
        fontWeight: isLeaf ? 400 : 'bold',
        color: contentColor,
      };
    }
  } else if (hasLeafProb) {
    const probText = edgeLabelsAlongPath.join(' \u00D7 ');
    nodeLabel = {
      show: true,
      position: 'bottom',
      distance: 6,
      formatter: probText,
      fontSize: 10,
      color: hl.onMatch ? ctx.matchColor : getTailwindColorValue('neutral', 500),
    };
  } else {
    nodeLabel = { show: false };
  }

  const node: EchartsTreeData = {
    name: def.id,
    symbol: 'circle',
    symbolSize: ctx.nodeRadius * 2,
    itemStyle: {
      color: hl.nodeColor,
      borderColor: hl.nodeStroke,
      borderWidth: 1,
      opacity: hl.nodeOpacity,
    },
    label: nodeLabel,
    collapsed: false,
    edgeLabelsFromRoot: edgeLabelsAlongPath,
  };

  // ── Recurse children ──
  if (def.children && def.children.length > 0) {
    node.children = def.children.map((edge, i) =>
      buildEchartsData(edge.node, edge.label, [...currentPath, i], [...edgeLabelsAlongPath, edge.label], ctx),
    );
  }

  if (!isRoot) {
    node.lineStyle = { color: hl.edgeColor, width: hl.edgeWidth, curveness: 0 };
  }

  return node;
}

function buildChartOptions(
  params: TreeParams,
  matchingPaths: TreePath[],
  matchingPathColors: TreePathColors[],
  hoveredPath: TreePath | null,
  colorName: ThemeColorName,
  isDark: boolean,
  labelBg: string,
): EChartsOption {
  const depth = computeDepth(params.root);
  const nodeRadius = params.display?.nodeRadius ?? autoNodeRadius(depth);

  const ctx: BuildContext = {
    matchingPaths,
    matchingPathColors,
    hoveredPath,
    matchColor: getTailwindColorValue(colorName),
    hoverColor: getTailwindColorValue('teal'),
    nodeRadius,
    showLeafProb: params.display?.showLeafProbabilities ?? false,
    showLeafEdgeLabels: params.display?.showLeafEdgeLabels ?? false,
    isDark,
    labelBg,
  };

  const data = buildEchartsData(params.root, undefined, [], [], ctx);

  // Dynamic margins to prevent label cut-off
  const topMargin = 16;
  const bottomMargin = depth <= 2 ? 48 : depth <= 4 ? 56 : 64;
  const sideMargin = depth <= 3 ? 24 : 16;

  return {
    series: [
      {
        type: 'tree',
        layout: 'orthogonal',
        orient: 'TB',
        roam: params.display?.zoomEnabled !== false,
        expandAndCollapse: false,
        initialTreeDepth: -1,
        symbol: 'circle',
        symbolSize: nodeRadius * 2,
        edgeShape: 'curve',
        lineStyle: {
          color: getTailwindColorValue('neutral', 300),
          width: 1.5,
          curveness: 0,
        },
        labelLayout: {
          hideOverlap: true,
        },
        emphasis: {
          disabled: true,
        },
        data: [data],
        top: topMargin,
        bottom: bottomMargin,
        left: sideMargin,
        right: sideMargin,
        animationDuration: 300,
        animationEasing: 'cubicOut',
      },
    ],
    tooltip: {
      show: true,
      trigger: 'item',
      formatter: ((tooltipParams: Record<string, unknown>) => {
        const nodeData = tooltipParams['data'] as EchartsTreeData | undefined;
        if (!nodeData || nodeData.edgeLabelsFromRoot.length === 0) {
          return '';
        }
        const latex = nodeData.edgeLabelsFromRoot.join(' \\times ');
        try {
          return katex.renderToString(latex, { throwOnError: false });
        } catch {
          return nodeData.edgeLabelsFromRoot.join(' \u00D7 ');
        }
      }) as unknown as string,
      backgroundColor: 'rgba(255,255,255,0.92)',
      borderColor: getTailwindColorValue('neutral', 200),
      borderWidth: 1,
      padding: [6, 10],
      textStyle: { fontSize: 14 },
      extraCssText: 'border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); color: var(--neutral-700);',
    } as EChartsOption['tooltip'],
  };
}

// ── Build path lookup map ────────────────────────────────

function buildPathMap(root: TreeNodeDefinition): Map<string, TreePath> {
  const map = new Map<string, TreePath>();
  function walk(def: TreeNodeDefinition, path: number[]): void {
    map.set(def.id, path);
    def.children?.forEach((edge, i) => walk(edge.node, [...path, i]));
  }
  walk(root, []);
  return map;
}

function isLeafNode(def: TreeNodeDefinition, id: string): boolean {
  if (def.id === id) {
    return !def.children || def.children.length === 0;
  }
  for (const edge of def.children ?? []) {
    const result = isLeafNode(edge.node, id);
    if (result !== undefined) {
      return result;
    }
  }
  return false;
}

// ── Zoom constants ───────────────────────────────────────

const ZOOM_STEPS = [0.25, 0.33, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
const ZOOM_STEPS_REVERSED = [...ZOOM_STEPS].reverse();
const ZOOM_MIN = ZOOM_STEPS[0];
const ZOOM_MAX = ZOOM_STEPS[ZOOM_STEPS.length - 1];

interface EChartsInstance {
  on: (event: string, handler: (params: Record<string, unknown>) => void) => void;
  setOption: (option: Record<string, unknown>, notMerge?: boolean) => void;
  getWidth: () => number;
  getHeight: () => number;
  dispatchAction: (action: Record<string, unknown>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getModel: () => any;
}

interface LatexAnnotation {
  readonly id: string;
  readonly left: number;
  readonly top: number;
  readonly html: SafeHtml;
  readonly isLeaf: boolean;
}

/** Collect nodes whose content label uses LaTeX notation. */
function collectLatexNodes(
  root: TreeNodeDefinition,
): Array<{ id: string; latex: string; color?: ThemeColorName; isLeaf: boolean }> {
  const nodes: Array<{ id: string; latex: string; color?: ThemeColorName; isLeaf: boolean }> = [];
  function walk(def: TreeNodeDefinition): void {
    if (def.content?.label && isLatexLabel(def.content.label)) {
      const isLeaf = !def.children || def.children.length === 0;
      nodes.push({ id: def.id, latex: def.content.label, color: def.content.color, isLeaf });
    }
    def.children?.forEach((edge) => walk(edge.node));
  }
  walk(root);
  return nodes;
}

// ── Component ────────────────────────────────────────────

@Component({
  selector: 'dojo-tree-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  templateUrl: './tree-chart.component.html',
  styleUrl: './tree-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block w-full' },
  providers: [provideEchartsCore({ echarts })],
})
export class TreeChartComponent extends BaseEchartsDirective {
  private readonly sanitizer = inject(DomSanitizer);

  readonly params = input.required<TreeParams>();
  readonly matchingPaths = input<TreePath[]>([]);
  readonly matchingPathColors = input<TreePathColors[]>([]);
  readonly hoveredPath = input<TreePath | null>(null);
  readonly color = input<ThemeColorName>('sky');

  readonly pathHover = output<TreePath | null>();
  readonly latexAnnotations = signal<LatexAnnotation[]>([]);

  private chartInstance: EChartsInstance | null = null;
  private readonly zoom = signal(1);

  private readonly latexNodeInfo = computed(() => collectLatexNodes(this.params().root));

  readonly zoomEnabled = computed(() => this.params().display?.zoomEnabled !== false);
  readonly zoomPercentage = computed(() => `${Math.round(this.zoom() * 100)}%`);
  readonly zoomInDisabled = computed(() => this.zoom() >= ZOOM_MAX);
  readonly zoomOutDisabled = computed(() => this.zoom() <= ZOOM_MIN);

  private readonly pathMap = computed(() => buildPathMap(this.params().root));

  readonly chartOptions = computed(() => {
    const neutral50 = getComputedStyle(this.document.body).getPropertyValue('--neutral-50').trim() || '#f7f5f2';
    const options = buildChartOptions(
      this.params(),
      this.matchingPaths(),
      this.matchingPathColors(),
      this.hoveredPath(),
      this.color(),
      this.isDark(),
      neutral50,
    );
    return adaptEchartsOptions(options, this.document, this.isDark());
  });

  zoomIn(): void {
    const current = this.zoom();
    const next = ZOOM_STEPS.find((s) => s > current) ?? ZOOM_MAX;
    this.setZoom(next);
  }

  zoomOut(): void {
    const current = this.zoom();
    const next = ZOOM_STEPS_REVERSED.find((s) => s < current) ?? ZOOM_MIN;
    this.setZoom(next);
  }

  fitToScreen(): void {
    this.setZoom(1);
  }

  onChartInit(chart: unknown): void {
    const ec = chart as EChartsInstance;
    this.chartInstance = ec;

    ec.on('mouseover', (eventParams: Record<string, unknown>) => {
      const name = eventParams['name'] as string | undefined;
      if (!name) {
        return;
      }
      const path = this.pathMap().get(name);
      if (path && isLeafNode(this.params().root, name)) {
        this.pathHover.emit(path);
      }
    });

    ec.on('mouseout', (eventParams: Record<string, unknown>) => {
      const name = eventParams['name'] as string | undefined;
      if (!name) {
        return;
      }
      if (isLeafNode(this.params().root, name)) {
        this.pathHover.emit(null);
      }
    });

    // Track zoom changes from mouse wheel
    ec.on('treeRoam', (eventParams: Record<string, unknown>) => {
      const zoomDelta = eventParams['zoom'] as number | undefined;
      if (zoomDelta) {
        this.zoom.update((z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z * zoomDelta)));
      }
    });

    // Update LaTeX overlays after chart renders
    ec.on('finished', () => this.updateLatexAnnotations());
  }

  private setZoom(absoluteZoom: number): void {
    if (!this.chartInstance) {
      return;
    }
    this.zoom.set(absoluteZoom);
    this.chartInstance.setOption({ series: [{ zoom: absoluteZoom }] } as Record<string, unknown>);
  }

  private updateLatexAnnotations(): void {
    const nodes = this.latexNodeInfo();
    if (!this.chartInstance || nodes.length === 0) {
      this.latexAnnotations.set([]);
      return;
    }

    try {
      const seriesModel = this.chartInstance.getModel().getSeriesByIndex(0);
      const data = seriesModel.getData();

      const matchPaths = this.matchingPaths();
      const hoverPath = this.hoveredPath();
      const pathMap = this.pathMap();
      const colorName = this.color();
      const nodeRadius = this.params().display?.nodeRadius ?? autoNodeRadius(computeDepth(this.params().root));

      const positions: LatexAnnotation[] = [];

      for (const info of nodes) {
        const dataIndex = data.indexOfName(info.id);
        if (dataIndex < 0) {
          continue;
        }

        const layout = data.getItemLayout(dataIndex);
        if (!layout) {
          continue;
        }

        // Determine color based on match/hover state
        const nodePath = pathMap.get(info.id);
        const onMatch = nodePath && matchPaths.some((p) => isOnPath(nodePath, p));
        const onHover = nodePath && hoverPath ? isOnPath(nodePath, hoverPath) : false;

        let color: string;
        if (onHover) {
          color = getTailwindColorValue('teal');
        } else if (onMatch) {
          color = getTailwindColorValue(colorName);
        } else if (info.color) {
          color = getTailwindColorValue(info.color);
        } else {
          color = getTailwindColorValue('neutral', 600);
        }

        let html = katex.renderToString(info.latex, { throwOnError: false, output: 'html' });
        if (info.isLeaf) {
          html = html.replace(/font-weight:\s*bold/g, 'font-weight:400');
        }

        positions.push({
          id: info.id,
          left: layout.x,
          top: layout.y + nodeRadius + 4,
          html: this.sanitizer.bypassSecurityTrustHtml(`<span style="color: ${color}">${html}</span>`),
          isLeaf: info.isLeaf,
        });
      }

      this.latexAnnotations.set(positions);
    } catch {
      // Chart may not be ready yet
    }
  }
}
